import React from 'react';
import { Zap, Loader2, Check, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { WARM_PAGES, NARRATION_PAGE } from '../../lib/demoWarmup';

const ALL_PAGES = [...WARM_PAGES, NARRATION_PAGE];
import { runPrepareDemo, useDemoWarmup, useDemoReady, agoLabel } from '../../hooks/useDemoWarmup';

const slug = (p) => (p === '/' ? 'home' : p.replace(/^\//, '').replace(/\//g, '-'));

// Resync + silently pre-load Home and every Health page so they paint from cache for 24h.
// `variant="tile"` renders as a Tools-launcher tile; default renders as a Toolkit action row.
export default function DemoWarmupButton({ onTrack, variant = 'row' }) {
  const tid = variant === 'tile' ? 'toolkit-fab-prepare-demo' : 'toolkit-prepare-demo';
  const { user, viewAsOrg } = useAuth();
  const orgId = viewAsOrg?.id || user?.rbac?.organization_id || user?.organization_id || null;
  const { running, progress } = useDemoWarmup();
  const ready = useDemoReady();
  const doneCount = progress?.done.length ?? 0;
  const run = () => { onTrack?.('prepare-demo'); runPrepareDemo(orgId); };
  const label = running ? `Preparing demo… ${doneCount}/${ALL_PAGES.length}` : 'Prepare demo';

  const list = progress && (
    <ul className={`grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px] text-slate-500 ${variant === 'tile' ? 'px-4 pb-2' : 'ml-3'}`} data-testid={`${tid}-progress`}>
      {ALL_PAGES.map((p) => {
        const r = progress.done.find((d) => d.path === p.path);
        const active = progress.current?.path === p.path;
        return (
          <li key={p.path} className={`flex items-center gap-1 ${r?.ok ? 'text-teal-700' : r ? 'text-amber-700' : active ? 'text-slate-800' : ''}`} data-testid={`warm-${slug(p.path)}`}>
            {r?.ok ? <Check className="h-3 w-3" /> : r ? <X className="h-3 w-3" /> : active ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="inline-block h-3 w-3 rounded-full border border-slate-300" />}
            {p.label}
          </li>
        );
      })}
    </ul>
  );

  if (variant === 'tile') {
    return (
      <div className="flex flex-col" data-testid={tid}>
        <button type="button" onClick={run} disabled={running} className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-teal-50 rounded-xl transition-colors text-left whitespace-nowrap group disabled:opacity-70" data-testid={`${tid}-btn`} title="Resync, then pre-load Home and every Health page so they open instantly for 24h">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 text-white">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          </span>
          <span className="flex flex-col">
            <span className="text-sm font-semibold text-slate-900 group-hover:text-teal-700">{label}</span>
            <span className="text-[11px] text-slate-500" data-testid="toolkit-fab-prepare-demo-sub">{ready && !running ? `Cached ${agoLabel(ready.at)} · ${ready.views} views` : 'Fresh data, instant pages for 24h'}</span>
          </span>
        </button>
        {list}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid={tid}>
      <button onClick={run} disabled={running} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-60" data-testid={`${tid}-btn`} title="Resync, then pre-load Home and every Health page so they open instantly for 24h">
        {running ? <Loader2 className="h-4 w-4 animate-spin text-teal-500" /> : <Zap className="h-4 w-4 text-teal-500" />}
        <span>{label}</span>
      </button>
      {list}
    </div>
  );
}
